import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Observable, catchError, debounceTime, map, of, switchMap, take, finalize } from 'rxjs';
import { SignatureService } from 'src/app/main/pages/services/signature.service';
import HttpStatusCode from '../../enum/HttpStatusCode';

/**
 * References:
 * https://blog.angular-university.io/angular-custom-validators/
 * https://angular.io/guide/form-validation
 * https://blog.angular-university.io/rxjs-error-handling/
 * https://angular.dev/guide/forms/form-validation#creating-asynchronous-validators
 * https://careydevelopment.us/blog/angular-material-how-to-manually-mark-a-form-field-as-invalid
 */
export function itsNotYourId(
  signatureService: SignatureService
): AsyncValidatorFn {
  return (control: AbstractControl) => {
    let data = JSON.stringify({ id: control.value, password: 'any' });
    const formData = new FormData();
    formData.append('data', new Blob([data], { type: 'application/json' }));
    formData.append('file', new Blob());
    return signatureService?.save(formData).pipe(
      map(() => {
        return null;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.FORBIDDEN || error.status > 499)
          return of({ itsNotYourId: true });
        else return of(null);
      })
    );
  };
}

export const signatureExpired: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const parts = control.value.split('/');
  const dateInputFormat = `${parts[1]}/${parts[0]}/${parts[2]}`;
  let dueDate = new Date(dateInputFormat);
  let today = new Date();
  control.markAsTouched();
  if (today.getTime() > dueDate.getTime()) return { signatureExpired: true };
  return null;
}

export function checkSignature(
  signatureService: SignatureService,
  fileHandled: () => File,
  setLoading: (loading: boolean) => void): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const idField = control.get('id');
      const passwordField = control.get('password');
      const dueDateField = control.get('dueDate');
      const dataFile = fileHandled();
      setLoading(true);

      if( !idField ||
          !passwordField ||
          !dataFile) {
        setLoading(false);
        return of(null);
      }
      if(dueDateField?.value) {
        setLoading(false);
        return of(checkDueDate(dueDateField));
      }
      
      const formData = getFormData(idField, passwordField, dataFile);

      return of(passwordField.value).pipe(
        debounceTime(300),
        take(1),
        switchMap(() => {
          return signatureService.check(formData);
        }
        ),
        map(response => {
          const dueDateRes = response.data.dueDate;
          if (!dueDateRes || !dueDateField) return null;
          return checkDueDate(dueDateField, dueDateRes);          
        }),
        catchError((error: HttpErrorResponse) => {
          if(!dueDateField) return of({ invalid: true });
          return verifyErrorResponse(error, dueDateField);
        }),
        finalize(() => setLoading(false)));
    }
  }

function verifyErrorResponse(
  error: HttpErrorResponse,
  dueDateField: AbstractControl): Observable<ValidationErrors> {
  if (error.status === HttpStatusCode.NOT_ACCEPTABLE) {
      const dueDateResponse = error?.error?.data?.dueDate;
      const isDueDateValid = checkDueDate(
        dueDateField,
        dueDateResponse);
      return isDueDateValid ? 
        of(isDueDateValid) : 
        of({ invalid: true });
  }
  if (error.status === HttpStatusCode.CONFLICT) {
    const eMessage = error.error.message;
    const result =
      eMessage.includes('conflict exception') ? { conflict: true } :
      eMessage.includes('Bad password') ? { badPassword: true } :
      { isNotOwned: true };
    return of(result);
  }
  return of({ invalid: true });
}

function checkDueDate(
  dueDateField: AbstractControl,
  dueDateResponse?: string): ValidationErrors | null {
  if(dueDateResponse) {
    dueDateField.setValue(dueDateResponse);
    dueDateField.markAsDirty();
  }
  let parts = dueDateField.value.split('/');
  const dateInputFormat = `${parts[1]}/${parts[0]}/${parts[2]}`;
  let dueDate = new Date(dateInputFormat);
  let today = new Date();
  const isExpired = (today.getTime() > dueDate.getTime());
  if (today.getTime() > dueDate.getTime())
    return { signatureExpired: true };
  return null;
}

function getFormData(id: AbstractControl,
                      password: AbstractControl,
                      dataFile: File): FormData {
  let dataJson = {
    id: id.value,
    password: password.value }
  let data = JSON.stringify(dataJson);
  const formData = new FormData();
  formData.append('data', new Blob([data], { type: 'application/json' }));
  formData.append('file', dataFile);
  return formData;
}